// The four application options offered on NewApplication's "Application path"
// picker. They are NOT new wire values: each option is just a combination of
// the three fields the application already carries --
//
//   applicationPath   BUNDLED | MNSP_ENROLLMENT_ONLY
//   mnspEnrollment    MNSP network enrollment (MAR MNSP_BILLING section + EUA)
//   terminalRental    terminal equipment rental (MAR TERMINAL_RENTAL section)
//
// so the backend sees exactly the same applicationPath / mnspBundle /
// terminalRentalBundle it always has. The MAR is required whenever MNSP
// enrollment or terminal rental is on; without MNSP, the MAR's subject is the
// terminal rental, which is what "CardConnect + MAR" selects.

export type ApplicationPath = "BUNDLED" | "MNSP_ENROLLMENT_ONLY";

export type ApplicationOption =
  | "CARDCONNECT_ONLY"
  | "CARDCONNECT_MAR"
  | "MNSP_ENROLLMENT_ONLY"
  | "CARDCONNECT_MNSP_BUNDLE";

export interface ApplicationProducts {
  applicationPath: ApplicationPath;
  mnspEnrollment: boolean;
  terminalRental: boolean;
}

export const APPLICATION_OPTIONS: {
  value: ApplicationOption;
  title: string;
  detail: string;
}[] = [
  {
    value: "CARDCONNECT_ONLY",
    title: "CardConnect Only",
    detail: "Merchant account only. No MAR and no MNSP services.",
  },
  {
    value: "CARDCONNECT_MAR",
    title: "CardConnect + MAR",
    detail:
      "Merchant account plus terminal rental, acknowledged on the MAR. No MNSP services.",
  },
  {
    value: "MNSP_ENROLLMENT_ONLY",
    title: "MNSP Enrollment Only",
    detail:
      "MAR + End User Agreement only; never submitted to CardConnect.",
  },
  {
    value: "CARDCONNECT_MNSP_BUNDLE",
    title: "CardConnect + MNSP Bundle",
    detail:
      "Merchant account plus MNSP network enrollment (MAR + End User Agreement).",
  },
];

/** Which option the current fields represent. */
export function applicationOptionFor(
  products: ApplicationProducts,
): ApplicationOption {
  if (products.applicationPath === "MNSP_ENROLLMENT_ONLY")
    return "MNSP_ENROLLMENT_ONLY";
  if (products.mnspEnrollment) return "CARDCONNECT_MNSP_BUNDLE";
  return products.terminalRental ? "CARDCONNECT_MAR" : "CARDCONNECT_ONLY";
}

/**
 * The fields to set when an option is picked. Terminal rental is fixed by the
 * two CardConnect-without-MNSP options (off for CardConnect Only, on for
 * CardConnect + MAR) and left as the operator had it for the two MNSP options,
 * where it stays an independent add-on.
 */
export function productsForOption(
  option: ApplicationOption,
  current: ApplicationProducts,
): ApplicationProducts {
  switch (option) {
    case "CARDCONNECT_ONLY":
      return { applicationPath: "BUNDLED", mnspEnrollment: false, terminalRental: false };
    case "CARDCONNECT_MAR":
      return { applicationPath: "BUNDLED", mnspEnrollment: false, terminalRental: true };
    case "MNSP_ENROLLMENT_ONLY":
      return {
        applicationPath: "MNSP_ENROLLMENT_ONLY",
        mnspEnrollment: true,
        terminalRental: current.terminalRental,
      };
    case "CARDCONNECT_MNSP_BUNDLE":
      return {
        applicationPath: "BUNDLED",
        mnspEnrollment: true,
        terminalRental: current.terminalRental,
      };
  }
}

/**
 * The PDI managed-network order default an option implies, as the
 * pdiPrefs.sendPdiOrder value to set when it's picked. The two CardConnect
 * options promise no MNSP services, so they default the order OFF ("No") --
 * both are applicationPath=BUNDLED, where the path-only default
 * (pdiOrderWanted) would otherwise send one. The MNSP options reset to ""
 * (no explicit choice), i.e. the path default: on for the bundle, off for
 * enrollment-only. The operator can still change it in the PDI section.
 */
export function pdiOrderDefaultForOption(option: ApplicationOption): "" | "No" {
  return option === "CARDCONNECT_ONLY" || option === "CARDCONNECT_MAR" ? "No" : "";
}

/** Whether terminal rental is an operator choice (vs fixed by the option). */
export function rentalIsOptional(option: ApplicationOption): boolean {
  return option === "MNSP_ENROLLMENT_ONLY" || option === "CARDCONNECT_MNSP_BUNDLE";
}
